import { ReactNode, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import {
  ATTACHMENTS_VALUE_NAME,
  HTTPS_URL,
  portalCollectionOutputs,
  retrievalFieldsFor,
  scanItemFieldsFor,
} from 'components/inboundReferrals/constants';
import Button from 'components/shared/Button';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { useExtensionPresence } from 'hooks/useExtensionPresence';
import { usePortalConnections } from 'hooks/usePortalConnections';
import { usePortalSystems } from 'hooks/usePortalSystems';
import PortalConnection from 'models/PortalConnection';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { redispatchPortalRetrievals } from 'services/api/portalRedispatches';
import { indexPortalScripts, portalScriptQueryKeys, showPortalScript } from 'services/api/portalScripts';
import { workflowQueryKeys } from 'services/api/workflows';
import { runPortalRetrieval } from 'services/portalExtension';
import { Body, BodySmall, BodySmallBold } from 'styles/typography';

import PortalDetailStep from './PortalDetailStep';
import { BADGE_LABELS, manageablePortals, portalBadge, portalStatusLine } from './portalReadiness';

// A sweep's one output: the pointers it found. The recording never uses the values it produces —
// see the discard note in `recordSweep`.
const DISCOVERED_ITEMS = 'discovered_items';

export interface ManageConnectedPortalsTiming {
  // How often the Testing Login wait re-reads the connection rows. The verdict arrives through
  // the status mirror minutes later; there is no channel to subscribe to.
  verifyPollMs: number;
  // 🚨 The bounded wait is the NORMAL path for an MFA portal: otp_required / captcha_required /
  // login_confused write no state and push no mirror update, so nothing ever resolves for them.
  verifyTimeoutMs: number;
  // A recording the person abandons (closed the portal tab, walked away) delivers nothing at all.
  // Explicit cancel does deliver, so this only catches abandonment.
  recordingTimeoutMs: number;
}

export const DEFAULT_TIMING: ManageConnectedPortalsTiming = {
  verifyPollMs: 3000,
  verifyTimeoutMs: 90_000,
  recordingTimeoutMs: 15 * 60_000,
};

type Step =
  | { name: 'list' }
  | { name: 'detail' }
  | { name: 'testing'; baselineVerifiedAt: number; baselineSavedAt: number }
  | { name: 'verified' }
  | { name: 'rejected' }
  | { name: 'unconfirmed' }
  | { name: 'savedUnverifiable' }
  | { name: 'recording'; kind: RecordingKind }
  | { name: 'recordingComplete'; kind: RecordingKind }
  | { name: 'recordingFailed'; message: string };

// Which recording is in flight: a sweep (finding referrals) or a retrieval (reading one).
type RecordingKind = 'sweep' | 'retrieval';

interface Props {
  onClose: () => void;
  // Opened from a portal-level task card, which already knows which portal it is about.
  initialSourceSystem?: string | null;
  timing?: ManageConnectedPortalsTiming;
}

/**
 * `Manage Connected Portals` — ONE multi-step modal, not a set of flows: the portal list, one
 * portal's detail, the two blocking waits and their outcomes are steps of the same wizard, and
 * the waits share a shape because they are the same pattern with different copy.
 *
 * Everything durable lives on the server. The modal is a live view of the Connected Portals rows
 * plus the step the user is on — a save that lands after the window is closed still lands, and
 * the row is what tells them so.
 */
export default function ManageConnectedPortalsModal({ onClose, initialSourceSystem, timing = DEFAULT_TIMING }: Props) {
  const queryClient = useQueryClient();
  const { connections, isPending, isError } = usePortalConnections();
  const { systems: portalSystems } = usePortalSystems();
  const { present: extensionPresent } = useExtensionPresence();
  const portals = manageablePortals(connections);

  const [sourceSystem, setSourceSystem] = useState<string | null>(initialSourceSystem ?? null);
  const [step, setStep] = useState<Step>(initialSourceSystem ? { name: 'detail' } : { name: 'list' });

  const connection = portals.find((portal) => portal.sourceSystem === sourceSystem) ?? null;
  const name = connection?.displayName ?? 'this portal';

  // The wait resolves against the ROW, not against the save's response — the save endpoint always
  // answers `verified: false`, because the login attempt has not happened yet when it replies.
  const testing = step.name === 'testing';
  useEffect(() => {
    if (!testing) return;

    const poll = setInterval(
      () => queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.list() }),
      timing.verifyPollMs
    );
    const bail = setTimeout(() => setStep({ name: 'unconfirmed' }), timing.verifyTimeoutMs);

    return () => {
      clearInterval(poll);
      clearTimeout(bail);
    };
  }, [testing, queryClient, timing.verifyPollMs, timing.verifyTimeoutMs]);

  useEffect(() => {
    if (step.name !== 'testing' || !connection?.credentials) return;
    const { status, verifiedAt, savedAt } = connection.credentials;
    // 🚨 Rejected is terminal and offers NO retry: re-submitting a password a portal already
    // refused is how an account gets locked out. Re-entry is a deliberate act.
    //
    // Baseline-guarded like the verified branch, and for a sharper reason: the row a person is
    // MOST likely to be re-entering a password on is one that already reads `invalid`, so an
    // unguarded check would reject them the instant they submitted, without a login ever being
    // attempted — breaking the one path that exists to recover. The guard is `savedAt`, because
    // olio-integration mirrors a save as active-with-no-verification and stamps it: a row that is
    // `invalid` AND carries a save newer than this attempt's baseline can only be a fresh verdict
    // on this attempt.
    if (status === 'invalid' && savedAt && savedAt.getTime() > step.baselineSavedAt) setStep({ name: 'rejected' });
    // Newer than the verdict that was on the row when this save went out — immune to the clock
    // skew a "newer than now" comparison would carry, and to a late verdict about someone else's
    // earlier save.
    else if (verifiedAt && verifiedAt.getTime() > step.baselineVerifiedAt) setStep({ name: 'verified' });
  }, [step, connection]);

  const openPortal = (portal: PortalConnection) => {
    setSourceSystem(portal.sourceSystem);
    setStep({ name: 'detail' });
  };

  // `verifyAnchored` false means no login check will EVER run for this save — nothing recorded
  // and no portal home URL — so the wait would burn its whole timeout and end on "couldn't
  // confirm", reading as failure for a save that worked (OLIO-3131). Say "saved" instead.
  const startTesting = (verifyAnchored: boolean) => {
    if (!verifyAnchored) {
      setStep({ name: 'savedUnverifiable' });
      return;
    }
    setStep({
      name: 'testing',
      baselineVerifiedAt: connection?.credentials?.verifiedAt?.getTime() ?? 0,
      baselineSavedAt: connection?.credentials?.savedAt?.getTime() ?? 0,
    });
  };

  const recordingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(recordingTimeout.current ?? undefined), []);

  // Both record flows, one runner: arm the abandonment timeout, race it against the extension's
  // run, and translate the outcome into a step. The values a recording produces are DISCARDED —
  // there is no run to put them on; a settings-modal recording exists to produce a script only.
  //
  // The invalidations ride the recording itself, not the race: a recording that outlives the
  // abandonment timeout can still deliver, and the timeout copy sends the person to "check the
  // portal's status in this list" — which has to be true when they do. Nothing is archived: the
  // newest non-archived row wins, so a re-record supersedes by existing.
  const performRecording = async (kind: RecordingKind, start: () => Promise<unknown>) => {
    setStep({ name: 'recording', kind });
    try {
      const abandoned = new Promise<never>((_resolve, reject) => {
        recordingTimeout.current = setTimeout(() => reject(new Error('timed-out')), timing.recordingTimeoutMs);
      });

      const recording = start();
      // A handler, not part of the race: after an abandonment win, a late rejection from the
      // recording must not surface as an unhandled one.
      recording.catch(() => undefined);

      await Promise.race([recording, abandoned]);
      setStep({ name: 'recordingComplete', kind });
    } catch (error) {
      // The extension's own errors are written for people and render verbatim; a transport
      // failure's "Request failed with status code 500" is not, so it falls back.
      const message =
        !isAxiosError(error) && error instanceof Error && error.message
          ? error.message
          : 'The recording did not finish.';
      setStep({
        name: 'recordingFailed',
        message:
          message === 'timed-out'
            ? `Olio didn’t hear back from the recording for ${name}. If you finished it in the portal, your instructions may still have been saved — check the portal’s status in this list.`
            : message,
      });
    } finally {
      clearTimeout(recordingTimeout.current ?? undefined);
      recordingTimeout.current = null;
    }
  };

  const recordSweep = async () => {
    if (!connection) return;
    const source = connection.sourceSystem;

    await performRecording('sweep', async () => {
      // The portal home a sweep walks: the configured front door when one exists (OLIO-3131),
      // else the newest discovery recording's own entry URL — the page it actually started on.
      const entryUrl = connection.homeUrl ?? (await newestDiscoveryEntryUrl(source));
      if (!HTTPS_URL.test(entryUrl)) throw new Error('Olio has no portal address to record against.');

      // `purpose` is sent explicitly and always: it defaults to `retrieval` at three independent
      // layers, so omitting it here would silently file this sweep as a per-referral script.
      return runPortalRetrieval({
        record: true,
        entryUrl,
        params: {},
        context: { sourceSystem: source, purpose: 'discovery' },
        expectedOutputs: [DISCOVERED_ITEMS],
        itemFields: scanItemFieldsFor(portalSystems, source),
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
        queryClient.invalidateQueries({ queryKey: portalScriptQueryKeys.list(source, 'discovery') });
      });
    });
  };

  const newestDiscoveryEntryUrl = async (source: string) => {
    const scripts = await indexPortalScripts(source, undefined, 'discovery');
    const newest = scripts[0];
    if (!newest) throw new Error('Olio has no portal address to record against.');
    return (await showPortalScript(newest.id)).entryUrl;
  };

  // A retrieval recording off the newest usable referral row (OLIO-3131). No `purpose` in the
  // context (absent means retrieval), and deliberately no workflow/action in it either: the
  // extension ignores them, and this recording must produce a script WITHOUT staging captured
  // values onto anyone's referral — that tail belongs to the task flow alone.
  const recordRetrieval = async () => {
    if (!connection?.latestRetrieval) return;
    const source = connection.sourceSystem;
    const { entryUrl, patientName } = connection.latestRetrieval;

    await performRecording('retrieval', () => {
      const outputFields = portalCollectionOutputs(
        retrievalFieldsFor(portalSystems, source).map((field) => field.name)
      );
      const expectedOutputs = outputFields.map((output) => output.name);
      return runPortalRetrieval({
        record: true,
        entryUrl,
        params: { patient_name: patientName ?? '' },
        context: { sourceSystem: source },
        expectedOutputs,
        documentOutputs: expectedOutputs.filter((outputName) => outputName === ATTACHMENTS_VALUE_NAME),
        outputFields,
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
        queryClient.invalidateQueries({ queryKey: portalScriptQueryKeys.list(source) });
      });
    });
  };

  const backToList = () => {
    setSourceSystem(null);
    setStep({ name: 'list' });
  };

  return (
    <StandardModal title='Manage Connected Portals' onCancel={onClose} maxWidth='620px'>
      {step.name === 'list' && (
        <ListStep portals={portals} isPending={isPending} isError={isError} onSelect={openPortal} onClose={onClose} />
      )}

      {step.name === 'detail' &&
        (connection ? (
          <PortalDetailStep
            connection={connection}
            extensionPresent={extensionPresent}
            onBack={backToList}
            onSaved={startTesting}
            onRecordSweep={recordSweep}
            onRecordRetrieval={recordRetrieval}
          />
        ) : (
          <Missing onBack={backToList} pending={isPending} />
        ))}

      {step.name === 'testing' && (
        <BlockingWait
          testId='testing-login'
          title={`Signing in to ${name}…`}
          detail={`Olio is trying your username and password against ${name} right now.`}
        />
      )}

      {step.name === 'recording' && (
        <BlockingWait
          testId='recording-in-progress'
          title='Portal instruction recording in progress…'
          detail={
            step.kind === 'sweep'
              ? `Follow the steps shown in the Olio extension to provide instructions for finding new referrals in ${name}`
              : `Follow the steps shown in the Olio extension to provide instructions for reading a referral in ${name}`
          }
        />
      )}

      {step.name === 'verified' && (
        <Outcome
          testId='login-verified'
          title={`Olio signed in to ${name}.`}
          detail={`Your sign-in works. Olio will use it to work ${name} on its own.`}
          onDone={backToList}>
          {connection && <StalledRetrievalsPrompt connection={connection} />}
        </Outcome>
      )}

      {step.name === 'rejected' && (
        <Outcome
          testId='login-rejected'
          title={`${name} rejected your sign-in.`}
          // No retry affordance anywhere on this panel — the only way forward is a deliberate
          // re-entry of the credentials.
          detail='Check the username and password, then enter them again. Olio will not retry the ones it has.'
          doneLabel='Enter them again'
          onDone={() => setStep({ name: 'detail' })}
        />
      )}

      {step.name === 'unconfirmed' && (
        <Outcome
          testId='login-unconfirmed'
          title={`Olio couldn’t confirm the sign-in for ${name}.`}
          // ⚠️ Undrawn state, plain copy pending Design. It is NOT an error: a portal that asks
          // for a code or a captcha at login never reports back at all, and the credentials are
          // saved either way.
          detail={`Your sign-in is saved. ${name} may have asked for a security code or a captcha, which Olio can’t answer on its own — it will try again the next time it works this portal.`}
          onDone={backToList}
        />
      )}

      {step.name === 'savedUnverifiable' && (
        <Outcome
          testId='login-saved-unverifiable'
          title={`Your sign-in for ${name} is saved.`}
          detail={`Olio can’t test it yet — there are no portal instructions to sign in with. It will be checked the first time Olio works ${name}.`}
          onDone={backToList}
        />
      )}

      {step.name === 'recordingComplete' && (
        <Outcome
          testId='recording-complete'
          title={`Your instructions for ${name} have been successfully recorded.`}
          detail={
            step.kind === 'sweep'
              ? `Olio will use your new instructions to find new referrals in ${name}.`
              : `Olio will use your new instructions to read referrals in ${name}.`
          }
          onDone={backToList}>
          {connection && <StalledRetrievalsPrompt connection={connection} />}
        </Outcome>
      )}

      {step.name === 'recordingFailed' && (
        <Outcome
          testId='recording-failed'
          title='The recording didn’t finish.'
          detail={step.message}
          onDone={backToList}
        />
      )}
    </StandardModal>
  );
}

function ListStep({
  portals,
  isPending,
  isError,
  onSelect,
  onClose,
}: {
  portals: PortalConnection[];
  isPending: boolean;
  isError: boolean;
  onSelect: (portal: PortalConnection) => void;
  onClose: () => void;
}) {
  return (
    <>
      <Prompt>
        Select a portal from the list below to manage your login credentials or re-record what fields you wish for Olio
        to capture during automated scans.
      </Prompt>
      {isError ? (
        <Message data-testid='connected-portals-error'>We couldn’t load your connected portals.</Message>
      ) : isPending ? (
        <Message data-testid='connected-portals-loading'>Loading portals…</Message>
      ) : portals.length === 0 ? (
        <Message data-testid='connected-portals-empty'>No portals are set up for automation yet.</Message>
      ) : (
        <Rows data-testid='connected-portals-list'>
          {portals.map((portal) => {
            const badge = portalBadge(portal);
            return (
              <Row
                key={portal.sourceSystem}
                type='button'
                onClick={() => onSelect(portal)}
                data-testid='connected-portal-row'
                data-source-system={portal.sourceSystem}>
                <RowHeader>
                  <RowName>{portal.displayName}</RowName>
                  {/* No badge at all for a saved-but-unconfirmed portal: VERIFIED would claim a
                      confirmation we never got, NEEDS ACTION would ask for something the user
                      already did. */}
                  {badge && (
                    <Badge $verified={badge === 'verified'} data-testid='connected-portal-badge'>
                      {BADGE_LABELS[badge]}
                    </Badge>
                  )}
                </RowHeader>
                <RowStatus data-testid='connected-portal-status'>{portalStatusLine(portal)}</RowStatus>
              </Row>
            );
          })}
        </Rows>
      )}
      <Actions>
        <Button onClick={onClose} data-testid='connected-portals-close'>
          Close
        </Button>
      </Actions>
    </>
  );
}

// The row this modal was opened against is gone (mode changed, or the admin unconfigured it).
function Missing({ onBack, pending }: { onBack: () => void; pending: boolean }) {
  if (pending) return <Message data-testid='connected-portals-loading'>Loading portals…</Message>;
  return (
    <>
      <Message data-testid='connected-portal-missing'>That portal is no longer set up for automation.</Message>
      <Actions>
        <Button variant='ghost' onClick={onBack}>
          Back
        </Button>
      </Actions>
    </>
  );
}

/**
 * Both waits, one component. There is nothing to cancel: the work is happening in another
 * process (a headless login, or a person in the portal tab), and closing the window does not
 * stop it — it only stops this view of it.
 */
function BlockingWait({ testId, title, detail }: { testId: string; title: string; detail: string }) {
  return (
    <Centered data-testid={testId}>
      <WaitTitle>{title}</WaitTitle>
      <WaitDetail>{detail}</WaitDetail>
      <WaitWarning>Please don’t close this window.</WaitWarning>
    </Centered>
  );
}

function Outcome({
  testId,
  title,
  detail,
  doneLabel = 'Close',
  onDone,
  children,
}: {
  testId: string;
  title: string;
  detail: string;
  doneLabel?: string;
  onDone: () => void;
  children?: ReactNode;
}) {
  return (
    <Centered data-testid={testId}>
      <WaitTitle>{title}</WaitTitle>
      <WaitDetail>{detail}</WaitDetail>
      {children}
      <Actions>
        <Button onClick={onDone} data-testid={`${testId}-done`}>
          {doneLabel}
        </Button>
      </Actions>
    </Centered>
  );
}

/**
 * The re-run offer (OLIO-3130): renders only when the connection row says stalled retrievals
 * exist AND a re-dispatch would actually go headless — both derived server-side, so a zero
 * count is simply no prompt.
 */
function StalledRetrievalsPrompt({ connection }: { connection: PortalConnection }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'failed'>('idle');
  // Until the person confirms, the count tracks the LIVE row — so an outcome screen that
  // renders before the recording's invalidations resettle simply updates in place, rather
  // than freezing whatever the race happened to serve first. The snapshot is taken at the
  // click: the redispatch itself zeroes the live count, and the in-flight/success copy
  // still needs to say how many were sent.
  const [confirmed, setConfirmed] = useState<number | null>(null);
  const count = confirmed ?? connection.stalledRetrievals;

  if (count === 0) return null;
  const referrals = count === 1 ? '1 referral was' : `${count} referrals were`;

  const rerun = async () => {
    setConfirmed(connection.stalledRetrievals);
    setState('running');
    try {
      await redispatchPortalRetrievals(connection.sourceSystem);
      queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
      setState('done');
    } catch {
      setState('failed');
    }
  };

  if (state === 'done') {
    return <WaitDetail data-testid='stalled-rerun-done'>Olio is re-running them now.</WaitDetail>;
  }

  return (
    <RerunOffer data-testid='stalled-rerun-offer'>
      <WaitDetail>
        {state === 'failed'
          ? 'The re-run didn’t start. You can try again.'
          : `${referrals} waiting on this and can now be worked automatically.`}
      </WaitDetail>
      <Button size='small' onClick={rerun} disabled={state === 'running'} data-testid='stalled-rerun-confirm'>
        {state === 'running' ? 'Re-running…' : `Re-run ${count === 1 ? 'it' : 'them'}`}
      </Button>
    </RerunOffer>
  );
}

const Prompt = styled(BodySmall)`
  display: block;
  color: ${({ theme }) => theme.colors.black75};
  margin-bottom: 20px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Row = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: left;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RowName = styled(Body).attrs({ as: 'span' })`
  color: ${({ theme }) => theme.colors.black100};
`;

const Badge = styled(BodySmallBold)<{ $verified: boolean }>`
  font-size: 11px;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 10px;
  color: ${({ theme, $verified }) => ($verified ? theme.colors.green100 : theme.colors.black75)};
  background: ${({ theme, $verified }) => ($verified ? theme.colors.green10 : theme.colors.yellow10)};
`;

const RowStatus = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Message = styled(BodySmall)`
  display: block;
  padding: 24px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.black75};
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  text-align: center;
`;

const WaitTitle = styled(Body).attrs({ as: 'p' })`
  color: ${({ theme }) => theme.colors.black100};
`;

const WaitDetail = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
`;

const WaitWarning = styled(BodySmallBold)`
  color: ${({ theme }) => theme.colors.black75};
  margin-top: 8px;
`;

const RerunOffer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;
